import { Component, OnInit, EventEmitter, Input, Output } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { HTTPService } from 'app/services/http.service';
import { calculationTypes } from 'app/constants/customer';
import { currency } from 'app/constants/currency.constants';
import { DataService, FormatService, HelperService } from 'app/services';

import { escapeRegExp, find, sortBy } from 'lodash';

@Component({
  selector: 'my-fleet-override-setting',
  templateUrl: './my-fleet-override-setting.component.html',
  styleUrls: ['./my-fleet-override-setting.component.scss'],
})
export class MyFleetOverrideSetting implements OnInit {
  @Input('user')
  set setUser(user: any) {
    if (user && user._id) {
      this.customer = user;
      this.setFleetSettings();
    }
  }
  @Input() errors: any = {};
  @Input() displayName: string = '';
  // @Input() isBorderThemeCheck: string = 'primary';
  @Input() isMyFleetCenter: boolean = false;

  public get inputTheme(): string {
    return this.isMyFleetCenter ? 'border' : 'primary';
  }
  @Input() fleetSettings = {
    isOrderDiscOverride: false,
    isShopSuppliesOverride: false,
    isTaxCodeOverride: false,
    isLaborRateOverride: false,
    isPricingMatrixOverride: false,
    customLaborRate: null,
    customPartPricingMatrix: null,
    customShopSuppliesType: null,
    customShopSuppliesValue: '',
    customShopSuppliesCap: '',
    customOrderDiscountType: null,
    customOrderDiscountValue: '',
    customOrderDiscountCap: '',
    customTaxCode: null,
  };

  @Output() handleDataChange = new EventEmitter<any>();

  isSubmitting: boolean = false;
  calculationTypes = calculationTypes;
  focus: any = {};
  fleetData = {};
  customer: any = {};
  partPricings: any[] = [];
  laborPricings: any[] = [];
  taxCodes = [];
  usd: string = currency.USD;
  isFleetTaxOverRideEnabled: boolean;

  constructor(private httpService:
    HTTPService,
    private formatService: FormatService,
    private helperService: HelperService, private currencyPipe: CurrencyPipe, private dataService: DataService) { }

  ngOnInit() {
    this.isFleetTaxOverRideEnabled = this.dataService.isFleetTaxOverRideEnabled;
    this.getPricingMatrix();
    this.getTaxCodes();
  }

  handleFocus(name: string) {
    this.focus[name] = true;
    this.errors[name] = '';
  }

  handleBlur(name: string) {
    this.focus[name] = false;
  }

  handleAppSettingChange(key: string, value: any) {
    this.fleetSettings[key] = value;
    this.handleDataChange.emit({ key, value, fleetSettings: this.fleetSettings });

    if (['customOrderDiscountType', 'customShopSuppliesType', 'customPartPricingMatrix', 'customLaborRate', 'customTaxCode'].includes(key)) {
      this.errors[key] = '';
    }

    if (key === 'isOrderDiscOverride' && !this.fleetSettings.isOrderDiscOverride) {
      this.errors.customOrderDiscountType = '';
      this.errors.customOrderDiscountValue = '';
      this.errors.customOrderDiscountCap = '';
    }

    if (key === 'isShopSuppliesOverride') {
      this.errors.customShopSuppliesType = '';
      this.errors.customShopSuppliesValue = '';
      this.errors.customShopSuppliesCap = '';
    }

    if (key === 'isTaxCodeOverride') {
      this.errors.customTaxCode = '';
    }

    if (key === 'isLaborRateOverride') {
      this.errors.customLaborRate = '';
    }

    if (key === 'isPricingMatrixOverride') {
      this.errors.customPartPricingMatrix = '';
    }
  }

  setFleetSettings() {
    if (!this.customer._id) return;

    this.fleetSettings = {
      ...this.fleetSettings,
      isOrderDiscOverride: this.customer.isOrderDiscOverride,
      isShopSuppliesOverride: this.customer.isShopSuppliesOverride,
      isLaborRateOverride: this.customer.isLaborRateOverride,
      isPricingMatrixOverride: this.customer.isPricingMatrixOverride,
      isTaxCodeOverride: this.customer.isTaxCodeOverride,
    };

    if (this.customer.customLaborRate) {
      this.fleetSettings.customLaborRate = find(this.laborPricings, { _id: this.customer.customLaborRate });
    }

    if (this.customer.customPartPricingMatrix) {
      this.fleetSettings.customPartPricingMatrix = find(this.partPricings, { _id: this.customer.customPartPricingMatrix });
    }

    if (this.customer.customOrderDiscount && this.customer.customOrderDiscount.type) {
      this.fleetSettings.customOrderDiscountType = find(this.calculationTypes, { value: this.customer.customOrderDiscount.type });
      this.fleetSettings.customOrderDiscountValue = this.customer.customOrderDiscount.value.toString();
      this.fleetSettings.customOrderDiscountCap = this.customer.customOrderDiscount.cap.toString();
    }

    if (this.customer.customShopSupplies && this.customer.customShopSupplies.type) {
      this.fleetSettings.customShopSuppliesType = find(this.calculationTypes, { value: this.customer.customShopSupplies.type });
      this.fleetSettings.customShopSuppliesValue = this.customer.customShopSupplies.value.toString();
      this.fleetSettings.customShopSuppliesCap = this.customer.customShopSupplies.cap.toString();
    }

    if (this.customer.customTaxCode && this.taxCodes.length) {
      this.fleetSettings.customTaxCode = find(this.taxCodes, { _id: this.customer.customTaxCode });
    }

    this.handleDataChange.emit({ fleetSettings: this.fleetSettings });
  }

  getPricingMatrix() {
    this.httpService.getMultiplePricings(['part', 'labor']).then((pricings: any[]) => {
      if (pricings && pricings.length) {
        sortBy(pricings, 'name').forEach((pricing: any) => {
          if (pricing.type === 'part') {
            this.partPricings.push(pricing);
          } else {
            pricing.name += ` - ${this.currencyPipe.transform(pricing.rate || 0)}`;
            this.laborPricings.push(pricing);
          }
        });
      }

      this.setFleetSettings();
    });
  }

  getTaxCodes() {
    this.httpService.getTaxCodes().then((taxCodes: any) => {
      if (taxCodes && taxCodes.record && taxCodes.record.length) {
        this.taxCodes = [...taxCodes.record.filter(({ status }) => status)];
        this.taxCodes.forEach((tax: any) => {
          tax.label = `${tax.name} - ${tax.salesTaxRate}%`;
        });
        this.setFleetSettings();
      }
    })
  }

  handleSave({ name: rate }) {
    const pricing = { type: 'labor', rate: Number(rate), name: '' };

    if (this.customer.fullNameQBO || this.displayName) {
      pricing.name = `Custom_${this.customer.fullNameQBO || this.displayName}`;

      const customerPricingsCount = this.laborPricings.filter(({ name }) => {
        return new RegExp(`^${escapeRegExp(`${pricing.name}`)}`, 'i').test(name);
      }).length;
      pricing.name += `_${customerPricingsCount + 1}`;
    } else {
      pricing.name = `Custom labor ${rate}`;
    }

    this.httpService.createLaborPricings(pricing).then(
      (pricing: any) => {
        if (pricing) {
          pricing.name += ` - ${this.currencyPipe.transform(pricing.rate || 0)}`;
          this.laborPricings = sortBy([...this.laborPricings, pricing], 'name');
          this.handleAppSettingChange('customLaborRate', pricing);
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }
  addPercentageOnDiscountFocus() {
    if (this.fleetSettings?.customOrderDiscountType?.value === 'percentage') {
      if (this.focus.customOrderDiscountValue) {
        return this.fleetSettings?.customOrderDiscountValue;
      } else {
        if (this.fleetSettings?.customOrderDiscountValue) {
          return this.fleetSettings?.customOrderDiscountValue + '%'
        }
      }
    }

    return this.formatService.formatCurrency(parseInt(this.fleetSettings?.customOrderDiscountValue));
  }

  addPercentageOrCurrency() {
    return this.fleetSettings?.customOrderDiscountType?.value === 'percentage'
      ? (this.focus.customOrderDiscountValue
        ? this.fleetSettings?.customOrderDiscountValue
        : this.fleetSettings?.customOrderDiscountValue && this.fleetSettings?.customOrderDiscountValue.slice(0, 3) + '%')
      : (this.focus.customOrderDiscountValue
        ? this.fleetSettings?.customOrderDiscountValue
        : this.fleetSettings?.customOrderDiscountValue && this.currencyPipe.transform(this.fleetSettings?.customOrderDiscountValue, 'USD'));
  }

  addShopSupply() {
    return this.fleetSettings?.customShopSuppliesType?.value === 'percentage'
      ? (this.focus.customShopSuppliesValue
        ? this.fleetSettings?.customShopSuppliesValue
        : this.fleetSettings?.customShopSuppliesValue && this.fleetSettings?.customShopSuppliesValue.slice(0, 3) + '%')
      : (this.focus.customShopSuppliesValue
        ? this.fleetSettings?.customShopSuppliesValue
        : this.fleetSettings?.customShopSuppliesValue && this.currencyPipe.transform(this.fleetSettings?.customShopSuppliesValue, 'USD'));
  }
}
